import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  inspectSubscription,
  isPushSupported,
  repairSubscription,
  subscribeToPush,
  unsubscribeFromPush
} from '../utils/pushSubscription';
import '../styles/InboxPushSubscription.css';

/**
 * Notification control in the customer's inbox.
 *
 * One tap to turn notifications on - no intermediate "are you sure" popup, the
 * browser's own permission dialog is the confirmation. Also self-heals: on
 * mount it repairs subscriptions that have quietly died (expired, rotated VAPID
 * key, or dropped server-side after a 410) without bothering the customer.
 */
function InboxPushSubscription({ iosPwaHandler }) {
  // loading | on | off | blocked | unsupported | ios-install
  const [status, setStatus] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const mountedRef = useRef(true);

  // True only for iOS Safari that has NOT been added to the Home Screen -
  // exactly the case where push cannot work yet.
  const needsIOSInstall = Boolean(iosPwaHandler?.isIOS);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (needsIOSInstall) {
      setStatus('ios-install');
      return;
    }
    if (!isPushSupported()) {
      setStatus('unsupported');
      return;
    }

    try {
      const { permission, subscription } = await inspectSubscription();
      if (!mountedRef.current) return;

      if (permission === 'denied') {
        setStatus('blocked');
        return;
      }
      if (!subscription) {
        setStatus('off');
        return;
      }

      // We have a subscription - make sure it is actually alive. This never
      // prompts, it only repairs.
      const { state } = await repairSubscription();
      if (!mountedRef.current) return;

      if (state === 'renewed') {
        setStatus('on');
        setFeedback({ kind: 'success', text: 'Your notifications were reconnected.' });
      } else if (state === 'ok') {
        setStatus('on');
      } else if (state === 'none') {
        setStatus('off');
      } else {
        // 'failed' - we know a subscription exists locally, so show it as on
        // rather than pushing the customer into a confusing state.
        setStatus('on');
      }
    } catch (err) {
      console.error('[InboxPush] Could not read subscription state:', err);
      if (mountedRef.current) setStatus('off');
    }
  }, [needsIOSInstall]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEnable = async () => {
    setBusy(true);
    setFeedback(null);

    const { state } = await subscribeToPush();
    if (!mountedRef.current) return;

    if (state === 'subscribed') {
      setStatus('on');
      setFeedback({ kind: 'success', text: "You're all set." });
    } else if (state === 'denied') {
      setStatus('blocked');
    } else if (state === 'dismissed') {
      setFeedback({ kind: 'info', text: 'No problem — you can turn these on any time.' });
    } else if (state === 'unsupported') {
      setStatus('unsupported');
    } else {
      setFeedback({ kind: 'error', text: 'Something went wrong. Please try again.' });
    }

    setBusy(false);
  };

  const handleDisable = async () => {
    setBusy(true);
    setFeedback(null);

    const { state } = await unsubscribeFromPush();
    if (!mountedRef.current) return;

    if (state === 'unsubscribed') {
      setStatus('off');
      setFeedback({ kind: 'info', text: 'Notifications turned off.' });
    } else {
      setFeedback({ kind: 'error', text: 'Could not turn notifications off. Please try again.' });
    }

    setBusy(false);
  };

  if (status === 'loading') return null;

  const renderControl = () => {
    switch (status) {
      case 'on':
        return (
          <>
            <div className="ips-text">
              <span className="ips-label">Notifications are on</span>
              <span className="ips-hint">You'll hear about rewards, specials and news.</span>
            </div>
            <button
              type="button"
              className="ips-btn ips-btn-ghost"
              onClick={handleDisable}
              disabled={busy}
            >
              {busy ? '…' : 'Turn off'}
            </button>
          </>
        );

      case 'off':
        return (
          <>
            <div className="ips-text">
              <span className="ips-label">Turn on notifications</span>
              <span className="ips-hint">Get rewards, specials and news as they happen.</span>
            </div>
            <button
              type="button"
              className="ips-btn ips-btn-primary"
              onClick={handleEnable}
              disabled={busy}
            >
              {busy ? 'Just a moment…' : 'Turn on'}
            </button>
          </>
        );

      case 'blocked':
        return (
          <div className="ips-text">
            <span className="ips-label">Notifications are blocked</span>
            <span className="ips-hint">
              Your browser is blocking them. Allow notifications for this site in your browser
              settings, then come back.
            </span>
          </div>
        );

      case 'ios-install':
        return (
          <div className="ips-text">
            <span className="ips-label">Add the app to get notifications</span>
            <span className="ips-hint">
              On iPhone, tap Share (□↑) then "Add to Home Screen". Notifications work once the app
              is installed.
            </span>
          </div>
        );

      case 'unsupported':
      default:
        return (
          <div className="ips-text">
            <span className="ips-label">Notifications aren't available</span>
            <span className="ips-hint">This browser doesn't support them.</span>
          </div>
        );
    }
  };

  return (
    <div className={`ips-container ips-status-${status}`}>
      <div className="ips-row">
        <span className="ips-icon" aria-hidden="true">🔔</span>
        {renderControl()}
      </div>
      {feedback && (
        <p className={`ips-feedback ips-feedback-${feedback.kind}`} role="status">
          {feedback.text}
        </p>
      )}
    </div>
  );
}

export default InboxPushSubscription;
